import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

export interface ScopeTab<T extends string> {
  id: T;
  label: string;
  /// Standard count chip. Hidden when undefined and no `badge` is provided.
  count?: number;
  /// Optional override chip, replaces the default count when set (e.g. the
  /// "urgent" pill on Feedback's For-me tab uses warning tones instead of
  /// the regular brand-soft chip).
  badge?: ReactNode;
  locked?: boolean;
}

interface Props<T extends string> {
  /// Currently active tab id.
  scope: T;
  /// Tab list in render order.
  tabs: ReadonlyArray<ScopeTab<T>>;
  onChange: (next: T) => void;
  /// Required for a11y, labels the button group (e.g. "Feedback scope").
  ariaLabel: string;
  /// Trailing slot, primary page action (e.g. "Schedule 1:1") renders
  /// flush-right on the same row as the tab strip.
  actions?: ReactNode;
  /// `content` is the quieter in-page strip. `inline` sits in another strip's
  /// actions slot so two concerns share one row.
  tone?: "default" | "content" | "inline";
}

/// Shared scope strip used by Feedback, Todos, approvals, reviews, insights,
/// settings, payroll and recruitment. Per-feature wrap components build the
/// list with their own counts + visibility rules and pass it in, this
/// primitive owns the visual rhythm only.
///
/// Deliberately NOT the ARIA tabs pattern. It used to render
/// `role="tablist"` + `role="tab"` with no `aria-controls`, no `tabpanel` on
/// any consumer body, no roving tabindex and no arrow keys, which promised a
/// widget it did not implement. Implementing the real pattern is wrong for
/// what the consumers actually are: `settings-layout` navigates between
/// routes (tabs must not navigate), and Feedback / Todos / approvals /
/// review-status re-query ONE list rather than swapping between parallel
/// panels. The `actions` slot also used to render inside the tablist, which
/// put a non-tab child in a tablist and made any "tab 2 of 5" count wrong.
/// So this is what it is: a labelled group of buttons, matching
/// `<GoalScopeTabs>`, the sibling lens control that already does this.
export function ScopeTabs<T extends string>({
  scope,
  tabs,
  onChange,
  ariaLabel,
  actions,
  tone = "default",
}: Props<T>): ReactNode {
  return (
    <div
      className={cn(
        "flex flex-wrap items-end justify-between gap-2",
        tone === "inline" ? "" : "border-b border-border",
      )}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: reason: a scope strip is a button group, not a form fieldset. */}
      <div className="-mb-px flex flex-wrap items-end gap-1" role="group" aria-label={ariaLabel}>
        {tabs.map((t) => (
          <ScopeTabButton
            key={t.id}
            active={t.id === scope}
            label={t.label}
            count={t.count}
            badge={t.badge}
            locked={t.locked === true}
            tone={tone}
            onClick={() => onChange(t.id)}
          />
        ))}
      </div>
      {actions ? <div className="-mb-px flex items-center gap-2 pb-1.5">{actions}</div> : null}
    </div>
  );
}

function ScopeTabButton({
  active,
  label,
  count,
  badge,
  locked,
  tone,
  onClick,
}: {
  active: boolean;
  label: string;
  count?: number;
  badge?: ReactNode;
  locked: boolean;
  tone: "default" | "content" | "inline";
  onClick: () => void;
}): ReactNode {
  const compact = tone === "content" || tone === "inline";
  return (
    <button
      type="button"
      aria-pressed={active}
      aria-disabled={locked || undefined}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-2 border-b-2 font-medium outline-none transition-colors",
        compact ? "h-8 min-h-8 px-2.5 text-xs" : "min-h-11 px-3.5 text-sm md:h-9 md:min-h-9",
        "focus-visible:rounded-t-md focus-visible:ring-2 focus-visible:ring-ring",
        locked && "text-text-quaternary",
        !locked &&
          (active
            ? "border-primary text-text-primary hover:border-primary/80"
            : "border-transparent text-text-tertiary hover:text-text-secondary"),
        locked && "border-transparent",
      )}
    >
      {label}
      {badge !== undefined ? (
        badge
      ) : count !== undefined ? (
        <span
          className={cn(
            "inline-flex min-w-5 items-center justify-center rounded-full px-1.5 font-mono text-3xs font-semibold tabular-nums",
            active
              ? "bg-pulse-soft text-text-brand-strong"
              : "bg-surface-muted text-text-secondary",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}
